import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { healthColor, formatINR, PLATFORMS, PLATFORM_LABELS } from "@/lib/helpers";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Sparkle, Trash, X, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";

export default function Products() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("master");
  const [aiSug, setAiSug] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [connected, setConnected] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [starting, setStarting] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const r = await api.get("/products", { params: { q, limit: 100 } });
    setItems(r.data.items); setTotal(r.data.total);
  }, [q]);

  useEffect(() => {
    load();
    api.get("/platforms").then(r => setConnected(r.data.items.map(c => c.platform)));
  }, [load]);

  const openDetail = async (p) => {
    const r = await api.get(`/products/${p.id}`);
    setDetail(r.data); setDetailTab("master"); setAiSug(r.data.ai_suggestions || null);
  };

  const saveDetail = async (updates) => {
    const r = await api.patch(`/products/${detail.id}`, updates);
    setDetail(r.data); load(); toast.success("Saved");
  };

  const aiComplete = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/ai/complete-product", { product_id: detail.id });
      setAiSug(r.data.suggestions);
      toast.success("AI suggestions ready");
    } catch { toast.error("AI failed. Try again."); }
    finally { setAiLoading(false); }
  };

  const acceptField = async (field) => {
    try {
      await api.post(`/products/${detail.id}/accept-ai/${field}`);
      const r = await api.get(`/products/${detail.id}`);
      setDetail(r.data); setAiSug(r.data.ai_suggestions || {});
      load();
      toast.success("Accepted");
    } catch {
      // fallback for pre-bulk suggestions still in local state
      let val = aiSug[field];
      if (!val) return;
      if (["bullet_points","features","benefits","keywords"].includes(field) && !Array.isArray(val)) val = [val];
      await saveDetail({ [field]: val });
      setAiSug({ ...aiSug, [field]: null });
    }
  };

  const rejectField = async (field) => {
    try { await api.post(`/products/${detail.id}/reject-ai/${field}`); } catch {}
    setAiSug({ ...aiSug, [field]: null });
    toast("Suggestion rejected");
  };

  const bulkDelete = async () => {
    const ids = Object.keys(sel).filter(k => sel[k]);
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} products?`)) return;
    await api.post("/products/bulk-delete", { ids });
    setSel({}); load(); toast.success(`Deleted ${ids.length}`);
  };

  const runBulkAI = async () => {
    setStarting(true);
    try {
      const ids = Object.keys(sel).filter(k => sel[k]);
      const r = await api.post("/ai/bulk-complete-checked", { product_ids: ids });
      if (r.data.truncated) {
        toast(`Started with ${r.data.total} of ${r.data.requested} (daily AI limit)`);
      } else {
        toast.success(`AI job started · ${r.data.total} products queued`);
      }
      setBulkConfirm(false); setSel({});
      nav(`/jobs`);
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error(`Daily AI limit reached (${e.response.data.detail.used}/${e.response.data.detail.limit}). Resets tomorrow.`);
      } else {
        toast.error(e?.response?.data?.detail || "Could not start AI job");
      }
    }
    finally { setStarting(false); }
  };

  const selectedCount = Object.values(sel).filter(Boolean).length;

  return (
    <div data-testid="products-page" className="space-y-6 min-w-0">
      <div className="flex items-end justify-between flex-wrap gap-4 min-w-0">
        <div className="min-w-0">
          <div className="eyebrow mb-1">Master Product Catalog</div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter text-navy">
            {total} <span className="text-muted-foreground font-medium">products</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Every product gets a permanent MPC ID and health score.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {selectedCount > 0 && (
            <>
              <button data-testid="bulk-ai" onClick={() => setBulkConfirm(true)} className="btn-primary">
                <Sparkle size={14} weight="fill"/> AI Complete {selectedCount}
              </button>
              <button data-testid="bulk-delete" onClick={bulkDelete} className="btn-secondary" style={{ color: "hsl(var(--destructive))", borderColor: "hsl(var(--destructive)/.4)" }}>
                <Trash size={14}/> Delete {selectedCount}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"/>
        <input data-testid="products-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search by name, SKU, MPC ID, brand…"
          className="input-base pl-10"/>
      </div>

      <div className="card-base overflow-hidden min-w-0">
        <div className="scroll-x">
          <table className="table-base">
            <thead>
              <tr>
                <th className="w-10">
                  <input type="checkbox" data-testid="select-all"
                    checked={items.length > 0 && selectedCount === items.length}
                    onChange={(e)=>{ if(e.target.checked){ const s={}; items.forEach(p=>s[p.id]=true); setSel(s);} else setSel({}); }}/>
                </th>
                <th>Product</th>
                <th>MPC ID</th>
                <th>SKU</th>
                <th>Source</th>
                <th className="text-right">Price</th>
                <th className="text-right">Stock</th>
                <th className="text-center">Health</th>
                {PLATFORMS.map(p => <th key={p} className="text-center">{PLATFORM_LABELS[p].slice(0,3)}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} className="cursor-pointer" onClick={() => openDetail(p)}>
                  <td onClick={(e)=>e.stopPropagation()}>
                    <input type="checkbox" checked={!!sel[p.id]} onChange={(e)=>setSel({...sel,[p.id]:e.target.checked})} data-testid={`select-${p.id}`}/>
                  </td>
                  <td>
                    <div className="flex items-center gap-3 min-w-0">
                      {p.images?.[0]?.url
                        ? <img src={p.images[0].url} alt="" className="w-10 h-10 object-cover border border-border rounded shrink-0" onError={(e)=>{e.target.style.visibility='hidden';}}/>
                        : <div className="w-10 h-10 bg-secondary border border-border rounded shrink-0"/>}
                      <div className="min-w-0">
                        <div className="font-medium text-navy truncate flex items-center gap-2">
                          {p.name}
                          {p.ai_suggestions && Object.keys(p.ai_suggestions).length > 0 && (
                            <span className="pill pill-draft"><Sparkle size={10} weight="fill"/> AI</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{p.brand}</div>
                      </div>
                    </div>
                  </td>
                  <td className="font-mono-data text-xs text-muted-foreground">{p.mpc_id}</td>
                  <td className="font-mono-data text-xs text-muted-foreground">{p.sku || "—"}</td>
                  <td>
                    {p.source === "shopify"
                      ? <span className="pill pill-live">Shopify</span>
                      : <span className="pill pill-neutral">Master</span>}
                  </td>
                  <td className="text-right font-mono-data tabular-nums">{formatINR(p.selling_price)}</td>
                  <td className="text-right font-mono-data tabular-nums">{p.stock ?? 0}</td>
                  <td className="text-center">
                    <span className={`font-mono-data font-semibold ${healthColor(p.health_score||0)}`}>{p.health_score || 0}</span>
                  </td>
                  {PLATFORMS.map(pl => {
                    const isConn = connected.includes(pl);
                    const l = p.listings?.find(x => x.platform === pl);
                    const s = isConn ? (l?.status || "—") : "—";
                    return (
                      <td key={pl} className="text-center">
                        <span title={isConn ? s : `${pl} not connected`} className={`inline-block w-2 h-2 rounded-full ${!isConn?"bg-muted-foreground/20":s==="LIVE"?"bg-brand-green":s==="MISSING"?"bg-red-500":s==="DRAFT"?"bg-amber-500":"bg-muted-foreground/30"}`}/>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={12} className="text-center text-muted-foreground text-sm py-10">No products yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk AI confirmation */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>setBulkConfirm(false)}>
          <div className="bg-background border border-border rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={(e)=>e.stopPropagation()} data-testid="bulk-ai-confirm">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-orange/10 flex items-center justify-center shrink-0"><Sparkle size={18} weight="fill" className="text-orange"/></div>
              <div>
                <h2 className="font-display font-bold text-xl text-navy">Run AI Complete on {selectedCount} products?</h2>
                <p className="text-sm text-muted-foreground mt-1">This creates a background job. Only missing fields are generated — your existing data is never overwritten.</p>
              </div>
            </div>
            <div className="border border-border rounded-md p-3 bg-secondary/40 text-xs font-mono-data text-muted-foreground mb-4">
              Estimated: ~1 AI call per product ({selectedCount} total). You'll be able to review and accept each suggestion.
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={()=>setBulkConfirm(false)} className="btn-secondary">Cancel</button>
              <button data-testid="bulk-ai-confirm-btn" disabled={starting} onClick={runBulkAI} className="btn-primary">
                {starting ? "Starting…" : `Start AI job`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Right Drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setDetail(null)}>
          <div className="flex-1 bg-black/50 backdrop-blur-sm" />
          <div className="w-full max-w-2xl bg-background border-l border-border overflow-y-auto" onClick={(e)=>e.stopPropagation()} data-testid="product-detail-drawer">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between z-10 gap-3">
              <div className="min-w-0">
                <div className="eyebrow font-mono-data">{detail.mpc_id}{detail.source === "shopify" && " · Shopify"}</div>
                <h2 className="font-display font-bold text-2xl tracking-tight text-navy truncate">{detail.name}</h2>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`pill ${(detail.health_score||0)>=85?"pill-live":(detail.health_score||0)>=65?"pill-draft":"pill-missing"}`}>
                  Health {detail.health_score}
                </span>
                <button onClick={()=>setDetail(null)} className="btn-ghost" data-testid="close-drawer"><X size={18}/></button>
              </div>
            </div>

            <div className="px-6 pt-4 flex gap-6 border-b border-border">
              {[["master","Master Data"],["platform","Platform Data"],["ai","AI Complete"]].map(([t,l]) => (
                <button key={t} onClick={()=>setDetailTab(t)} data-testid={`tab-${t}`}
                  className={`pb-3 text-xs uppercase tracking-wider font-semibold border-b-2 transition-colors ${detailTab===t?"border-orange text-orange":"border-transparent text-muted-foreground hover:text-navy"}`}>
                  {l}
                  {t === "ai" && aiSug && Object.keys(aiSug).filter(k=>aiSug[k]).length > 0 && (
                    <span className="ml-1.5 pill pill-draft text-[9px] py-0.5 px-1.5">{Object.keys(aiSug).filter(k=>aiSug[k]).length}</span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-5">
              {detailTab === "master" && <MasterForm detail={detail} save={saveDetail}/>}
              {detailTab === "platform" && (
                <div className="space-y-3">
                  {(detail.listings || []).map(l => (
                    <div key={l.platform} className="card-base p-4">
                      <div className="flex items-center justify-between mb-3 gap-2">
                        <div className="font-display font-semibold text-navy">{PLATFORM_LABELS[l.platform]}</div>
                        <span className={`pill pill-${(l.status||"missing").toLowerCase()}`}>{l.status}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs font-mono-data">
                        <div><div className="eyebrow">Listing ID</div><div className="text-navy">{l.listing_id || "—"}</div></div>
                        <div><div className="eyebrow">Price</div><div className="text-navy">{formatINR(l.price)}</div></div>
                        <div><div className="eyebrow">Stock</div><div className="text-navy">{l.stock ?? "—"}</div></div>
                        <div><div className="eyebrow">Last sync</div><div className="text-navy">{l.last_sync ? new Date(l.last_sync).toLocaleString() : "—"}</div></div>
                      </div>
                      {l.platform === "shopify" && (
                        <button
                          data-testid={`publish-shopify-${detail.id}`}
                          onClick={async ()=>{
                            try {
                              const r = await api.post(`/integrations/shopify/publish/${detail.id}`);
                              toast.success(`${r.data.action === "create" ? "Published to" : "Updated on"} Shopify`);
                              const r2 = await api.get(`/products/${detail.id}`); setDetail(r2.data); load();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
                          }}
                          className="btn-primary py-1.5 px-3 text-[11px] mt-3">
                          {l.listing_id ? "Update on Shopify" : "Publish to Shopify"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {detailTab === "ai" && (
                <div>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <button data-testid="ai-complete-btn" disabled={aiLoading} onClick={aiComplete} className="btn-primary">
                      <Sparkle size={14} weight="fill"/> {aiLoading?"Thinking…":"Auto-fill missing info with AI"}
                    </button>
                    {aiSug && Object.keys(aiSug).filter(k=>aiSug[k]).length > 0 && (
                      <div className="pill pill-draft"><Warning size={11}/> Verify before publishing</div>
                    )}
                  </div>
                  {aiSug && Object.entries(aiSug).some(([_, v]) => v) && (
                    <div className="space-y-3">
                      {Object.entries(aiSug).map(([k, v]) => v && (
                        <div key={k} className="card-base p-4">
                          <div className="flex items-center justify-between mb-2 gap-2">
                            <div className="eyebrow truncate">{k.replace(/_/g," ")}</div>
                            <div className="flex gap-1.5 shrink-0">
                              <button data-testid={`accept-${k}`} onClick={() => acceptField(k)} className="btn-primary py-1 px-2 text-[10px]">
                                <CheckCircle size={12} weight="fill"/> Accept
                              </button>
                              <button onClick={() => rejectField(k)} className="btn-secondary py-1 px-2 text-[10px]">
                                <XCircle size={12}/> Reject
                              </button>
                            </div>
                          </div>
                          <div className="text-sm whitespace-pre-wrap font-mono-data leading-relaxed text-navy">
                            {Array.isArray(v)
                              ? v.map((x,i)=><div key={i}>• {typeof x === "object" ? JSON.stringify(x) : String(x)}</div>)
                              : typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MasterForm({ detail, save }) {
  const [f, setF] = useState({});
  useEffect(() => setF({ ...detail }), [detail.id]);
  const chg = (k, v) => setF({ ...f, [k]: v });
  const commit = () => {
    save({
      name: f.name, brand: f.brand, sku: f.sku, category: f.category, mrp: f.mrp,
      selling_price: f.selling_price, cost_price: f.cost_price, description: f.description,
      seo_title: f.seo_title, seo_description: f.seo_description, url_slug: f.url_slug,
      net_weight: f.net_weight, stock: f.stock,
      bullet_points: typeof f.bullet_points === "string" ? f.bullet_points.split("\n").filter(x=>x) : f.bullet_points,
      keywords: typeof f.keywords === "string" ? f.keywords.split(",").map(x=>x.trim()).filter(x=>x) : f.keywords,
    });
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ["name","Product Name"], ["brand","Brand"], ["sku","SKU"], ["category","Category"],
          ["net_weight","Net Weight"], ["url_slug","URL Slug"],
          ["mrp","MRP","number"], ["selling_price","Selling Price","number"],
          ["cost_price","Cost Price","number"], ["stock","Stock","number"],
        ].map(([k, label, type]) => (
          <div key={k} className="min-w-0">
            <label className="eyebrow block mb-1.5">{label}</label>
            <input data-testid={`field-${k}`} type={type||"text"} value={f[k]??""} onChange={(e)=>chg(k, type==="number"?parseFloat(e.target.value)||null:e.target.value)}
              className="input-base"/>
          </div>
        ))}
      </div>
      <div>
        <label className="eyebrow block mb-1.5">Description</label>
        <textarea data-testid="field-description" rows={4} value={f.description||""} onChange={(e)=>chg("description", e.target.value)} className="input-base"/>
      </div>
      <div>
        <label className="eyebrow block mb-1.5">Bullet Points (one per line)</label>
        <textarea rows={4} value={Array.isArray(f.bullet_points)?f.bullet_points.join("\n"):(f.bullet_points||"")} onChange={(e)=>chg("bullet_points", e.target.value)} className="input-base"/>
      </div>
      <div>
        <label className="eyebrow block mb-1.5">Keywords (comma separated)</label>
        <input value={Array.isArray(f.keywords)?f.keywords.join(", "):(f.keywords||"")} onChange={(e)=>chg("keywords", e.target.value)} className="input-base"/>
      </div>
      <div>
        <label className="eyebrow block mb-1.5">SEO Title</label>
        <input value={f.seo_title||""} onChange={(e)=>chg("seo_title", e.target.value)} className="input-base"/>
      </div>
      <button data-testid="save-product" onClick={commit} className="btn-primary">
        <CheckCircle size={14} weight="fill"/> Save changes
      </button>
    </div>
  );
}
